import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';

const TourBookingForm = ({ onClose, onSuccess, editLead = null }) => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);

  const [formData, setFormData] = useState(editLead ? {
    // Pre-fill with existing data
    leadType: editLead.leadType || 'tour_booking',
    serviceType: editLead.serviceType || '',
    assignedTo: editLead.assignedTo?._id || editLead.assignedTo || '',
    customerName: editLead.customerName || '',
    email: editLead.email || '',
    contactNumber: editLead.contactNumber || '',
    inquiryTime: editLead.inquiryTime ? new Date(editLead.inquiryTime).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
    tourCode: editLead.tourCode || '',
    reference: editLead.reference || '',
    inquiryType: editLead.inquiryType || 'Direct',
    referenceSource: editLead.referenceSource || '',
    otherInquiryDetails: editLead.otherInquiryDetails || '',
    tourType: editLead.tourType || 'domestic',
    destination: editLead.destination || '',
    travelDate: editLead.travelDate ? new Date(editLead.travelDate).toISOString().slice(0, 10) : '',
    numberOfNights: editLead.numberOfNights || '',
    numberOfPax: editLead.numberOfPax || '',
    numberOfChildren: editLead.numberOfChildren || '0',
    ageOfChildren: editLead.ageOfChildren || '',
    childrenAges: editLead.childrenAges || [],
    numberOfRooms: editLead.numberOfRooms || '',
    extraBed: editLead.extraBed || 'None',
    hotelCategory: editLead.hotelCategory || '3 Star',
    mealPlan: editLead.mealPlan || 'CP',
    vehicleType: editLead.vehicleType || '',
    tourPlan: editLead.tourPlan || '',
    pickUp: editLead.pickUp || '',
    drop: editLead.drop || '',
    reservationStatus: editLead.reservationStatus || 'Pending',
    specialNote: editLead.specialNote || ''
  } : {
    // Lead Type
    leadType: 'tour_booking',
    serviceType: '',
    assignedTo: '',

    // Basic Information
    customerName: '',
    email: '',
    contactNumber: '',

    // Tour Details
    inquiryTime: new Date().toISOString().slice(0, 16),
    tourCode: '',
    reference: '',
    inquiryType: 'Direct',
    referenceSource: '',
    otherInquiryDetails: '',
    tourType: 'domestic',
    destination: '',

    // Travel Details
    travelDate: '',
    numberOfNights: '',
    numberOfPax: '',
    numberOfChildren: '0',
    ageOfChildren: '',
    childrenAges: [],

    // Accommodation
    numberOfRooms: '',
    extraBed: 'None',
    hotelCategory: '3 Star',
    mealPlan: 'CP',

    // Transport & Itinerary
    vehicleType: '',
    tourPlan: '',
    pickUp: '',
    drop: '',

    // Status & Notes
    reservationStatus: 'Pending',
    specialNote: ''
  });

  const [loading, setLoading] = useState(false);
  const [itinerary, setItinerary] = useState([]);

  // Load employees for assignment
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
        const response = await axios.get('/employees', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const employeesData = response.data.data || response.data || [];
        setEmployees(Array.isArray(employeesData) ? employeesData : []);
      } catch (error) {
        console.error('Error loading employees:', error);
      }
    };
    loadEmployees();
  }, []);

  // Filter employees based on service type
  useEffect(() => {
    if (formData.leadType === 'other_services' && formData.serviceType) {
      const filtered = employees.filter(emp =>
        emp.teamType === 'other_services' &&
        emp.serviceSpecialization?.includes(formData.serviceType)
      );
      setFilteredEmployees(filtered);
    } else {
      setFilteredEmployees([]);
    }
  }, [formData.leadType, formData.serviceType, employees]);

  // Auto-generate tour code on component mount (only for new leads)
  useEffect(() => {
    if (!editLead && formData.leadType === 'tour_booking') {
      const fetchTourCode = async () => {
        try {
          const token = localStorage.getItem('token') ||
            localStorage.getItem('companyToken') ||
            localStorage.getItem('employeeToken');

          console.log('🎫 Fetching tour code...');
          const response = await axios.get('/leads/generate-tour-code', {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('✅ Tour code generated:', response.data.tourCode);
          setFormData(prev => ({ ...prev, tourCode: response.data.tourCode }));
        } catch (error) {
          console.error('❌ Error fetching tour code:', error);
          console.error('❌ Error response:', error.response?.data);
          // Set a fallback tour code
          const fallbackCode = `GT-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-000`;
          console.log('⚠️ Using fallback tour code:', fallbackCode);
          setFormData(prev => ({ ...prev, tourCode: fallbackCode }));
        }
      };
      fetchTourCode();
    }
  }, [editLead, formData.leadType]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If numberOfChildren changes, reset childrenAges array
    if (name === 'numberOfChildren') {
      const numChildren = parseInt(value) || 0;
      setFormData(prev => ({
        ...prev,
        [name]: value,
        childrenAges: new Array(numChildren).fill('')
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle individual child age change
  const handleChildAgeChange = (index, age) => {
    const newChildrenAges = [...formData.childrenAges];
    newChildrenAges[index] = age;
    setFormData(prev => ({
      ...prev,
      childrenAges: newChildrenAges
    }));
  };

  // Handle itinerary day change
  const handleItineraryChange = (dayIndex, value) => {
    const newItinerary = [...itinerary];
    newItinerary[dayIndex] = value;
    setItinerary(newItinerary);
  };

  // Update itinerary array when numberOfNights changes
  useEffect(() => {
    const nights = parseInt(formData.numberOfNights) || 0;
    const days = nights > 0 ? nights + 1 : 0; // Days = Nights + 1
    setItinerary(new Array(days).fill(''));
  }, [formData.numberOfNights]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      // Clean up data - remove empty serviceType for tour bookings
      const cleanedData = { ...formData };
      if (cleanedData.leadType === 'tour_booking' && (!cleanedData.serviceType || cleanedData.serviceType === '')) {
        delete cleanedData.serviceType; // Remove empty serviceType
      }

      console.log('📤 Submitting lead data:', cleanedData);

      if (editLead) {
        // Update existing lead
        const response = await axios.put(`/leads/${editLead._id}`, cleanedData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Lead updated:', response.data);
        alert('Lead updated successfully!');
      } else {
        // Create new lead
        const response = await axios.post('/leads', cleanedData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Lead created:', response.data);

        const createdLead = response.data.lead || response.data;

        console.log('✅ Lead created successfully');
        console.log('📋 Lead Type:', cleanedData.leadType);
        console.log('👥 Assigned To:', cleanedData.assignedTo);

        // Notification will be created by backend automatically

        // Auto-forward Tour Booking leads to Reservation
        if (cleanedData.leadType === 'tour_booking' && createdLead._id) {
          try {
            console.log('📤 Auto-forwarding tour booking lead to Reservation...');
            await axios.post(`/leads/${createdLead._id}/forward-to-reservation`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            console.log('✅ Lead auto-forwarded to Reservation team');
            alert('✅ Tour booking lead created and forwarded to Reservation team!');
          } catch (forwardError) {
            console.error('❌ Error auto-forwarding:', forwardError);
            alert('Lead created successfully, but failed to auto-forward to Reservation. Please forward manually.');
          }
        } else if (cleanedData.leadType === 'other_services') {
          alert('✅ Other Services lead created and assigned successfully!');
        } else {
          alert('Lead added successfully!');
        }
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('❌ Error saving lead:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error details:', error.response?.data?.details);

      const errorMessage = error.response?.data?.message || error.message;
      const errorDetails = error.response?.data?.error || '';
      const validationErrors = error.response?.data?.details ?
        '\n\nValidation Errors:\n' + JSON.stringify(error.response.data.details, null, 2) : '';

      alert('Error saving lead: ' + errorMessage + '\n' + errorDetails + validationErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {editLead
              ? (formData.leadType === 'tour_booking' ? 'Edit Tour Booking' : 'Edit Service Lead')
              : (formData.leadType === 'tour_booking' ? 'Add New Tour Booking' : 'Add New Service Lead')
            }
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Lead Type Selection */}
          <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              🎯 Lead Type
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Lead Type *
                </label>
                <select
                  name="leadType"
                  value={formData.leadType}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="tour_booking">🏖️ Tour Booking</option>
                  <option value="other_services">📋 Other Services (Passport, Ticket, Visa, etc.)</option>
                </select>
              </div>

              {/* Service Type - Only show for Other Services */}
              {formData.leadType === 'other_services' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Type *
                    </label>
                    <select
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Service Type</option>
                      <option value="passport">🛂 Passport Services</option>
                      <option value="visa">📄 Visa Services</option>
                      <option value="currency">💱 Currency Exchange / Forex</option>
                      <option value="railway_ticketing">🚂 Railway Ticketing</option>
                      <option value="bus_ticketing">🚌 Bus Ticketing</option>
                      <option value="flight_ticketing">✈️ Flight Ticketing</option>
                      <option value="cruise_ticketing">🚢 Cruise Ticketing</option>
                      <option value="car_rental">🚗 Car Rental</option>
                      <option value="insurance">🛡️ Travel Insurance</option>
                      <option value="other">📦 Other Services</option>
                    </select>
                  </div>

                  {/* Assign To Employee - Only show after service type is selected */}
                  {formData.serviceType && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        👤 Assign To Employee *
                      </label>
                      <select
                        name="assignedTo"
                        value={formData.assignedTo}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Employee</option>
                        {filteredEmployees.length > 0 ? (
                          filteredEmployees.map((emp) => (
                            <option key={emp._id} value={emp._id}>
                              {emp.teamMemberName || emp.name} - {emp.designation || emp.role}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>No employees available for this service</option>
                        )}
                      </select>
                      {filteredEmployees.length === 0 && formData.serviceType && (
                        <p className="text-xs text-red-600 mt-1">
                          ⚠️ No employees found with {formData.serviceType} specialization
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Basic Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
              📋 Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Name *
                </label>
                <input
                  type="text"
                  name="customerName"
                  required
                  value={formData.customerName}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Number *
                </label>
                <input
                  type="text"
                  name="contactNumber"
                  required
                  value={formData.contactNumber}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Tour Details - Only for Tour Booking */}
          {formData.leadType === 'tour_booking' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
                🎯 Tour Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tour Code (Auto-generated)
                  </label>
                  <input
                    type="text"
                    name="tourCode"
                    value={formData.tourCode}
                    readOnly
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-gray-700 font-semibold"
                    placeholder="Generating..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Inquiry Time
                  </label>
                  <input
                    type="datetime-local"
                    name="inquiryTime"
                    value={formData.inquiryTime}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Inquiry Type *
                  </label>
                  <select
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Direct">Direct</option>
                    <option value="Phone">Phone</option>
                    <option value="Email">Email</option>
                    <option value="Website">Website</option>
                    <option value="Walk-in">Walk-in</option>
                    <option value="Referral">Referral</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Conditional: Reference Source - Only show when Referral is selected */}
                {formData.inquiryType === 'Referral' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reference Source *
                    </label>
                    <input
                      type="text"
                      name="referenceSource"
                      value={formData.referenceSource}
                      onChange={handleChange}
                      required
                      placeholder="Enter reference name"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* Conditional: Other Details - Only show when Other is selected */}
                {formData.inquiryType === 'Other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Other Details *
                    </label>
                    <input
                      type="text"
                      name="otherInquiryDetails"
                      value={formData.otherInquiryDetails}
                      onChange={handleChange}
                      required
                      placeholder="Specify other inquiry type"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Travel Information - Only for Tour Booking */}
          {formData.leadType === 'tour_booking' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
                ✈️ Travel Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Tour Type - Prominent Display */}
                <div className="md:col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🌍 Tour Type *
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 px-6 py-3 border-2 rounded-lg cursor-pointer transition-all hover:border-blue-500 hover:bg-blue-50" style={{
                      borderColor: formData.tourType === 'domestic' ? '#3b82f6' : '#d1d5db',
                      backgroundColor: formData.tourType === 'domestic' ? '#eff6ff' : 'white'
                    }}>
                      <input
                        type="radio"
                        name="tourType"
                        value="domestic"
                        checked={formData.tourType === 'domestic'}
                        onChange={handleChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="font-semibold text-gray-900">🇮🇳 Domestic Tour</span>
                    </label>
                    <label className="flex items-center gap-2 px-6 py-3 border-2 rounded-lg cursor-pointer transition-all hover:border-blue-500 hover:bg-blue-50" style={{
                      borderColor: formData.tourType === 'international' ? '#3b82f6' : '#d1d5db',
                      backgroundColor: formData.tourType === 'international' ? '#eff6ff' : 'white'
                    }}>
                      <input
                        type="radio"
                        name="tourType"
                        value="international"
                        checked={formData.tourType === 'international'}
                        onChange={handleChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="font-semibold text-gray-900">🌏 International Tour</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Destination *
                  </label>
                  <input
                    type="text"
                    name="destination"
                    required
                    value={formData.destination}
                    onChange={handleChange}
                    placeholder={formData.tourType === 'domestic' ? 'e.g., Goa, Kerala, Rajasthan' : 'e.g., Dubai, Thailand, Singapore'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Travel Date *
                  </label>
                  <input
                    type="date"
                    name="travelDate"
                    required
                    value={formData.travelDate}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    No. of Nights
                  </label>
                  <input
                    type="number"
                    name="numberOfNights"
                    value={formData.numberOfNights}
                    onChange={handleChange}
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    No. of Pax (Adults)
                  </label>
                  <input
                    type="number"
                    name="numberOfPax"
                    value={formData.numberOfPax}
                    onChange={handleChange}
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    No. of Children (Max 10)
                  </label>
                  <input
                    type="number"
                    name="numberOfChildren"
                    value={formData.numberOfChildren}
                    onChange={handleChange}
                    min="0"
                    max="10"
                    title="Maximum 10 children allowed"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                  {parseInt(formData.numberOfChildren) > 10 && (
                    <p className="text-xs text-red-600 mt-1">⚠️ Maximum 10 children allowed</p>
                  )}
                </div>

                {/* Dynamic Children Age Inputs - Show individual fields for each child */}
                {parseInt(formData.numberOfChildren) > 0 && (
                  <>
                    {Array.from({ length: parseInt(formData.numberOfChildren) }, (_, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Child {index + 1} Age *
                        </label>
                        <input
                          type="number"
                          value={formData.childrenAges[index] || ''}
                          onChange={(e) => handleChildAgeChange(index, e.target.value)}
                          min="0"
                          max="18"
                          required
                          placeholder={`Age of child ${index + 1}`}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Accommodation Details - Only for Tour Booking */}
          {formData.leadType === 'tour_booking' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
                🏨 Accommodation Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    No. of Rooms
                  </label>
                  <input
                    type="number"
                    name="numberOfRooms"
                    value={formData.numberOfRooms}
                    onChange={handleChange}
                    min="0"
                    max="10"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Extra Bed
                  </label>
                  <select
                    name="extraBed"
                    value={formData.extraBed}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="None">None</option>
                    <option value="AWEB">AWEB (Adult With Extra Bed)</option>
                    <option value="CWEB">CWEB (Child With Extra Bed)</option>
                    <option value="CNB">CNB (Child With No Bed)</option>
                    <option value="CWB">CWB (Child With Bed)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hotel Category
                  </label>
                  <select
                    name="hotelCategory"
                    value={formData.hotelCategory}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="3 Star">3 Star</option>
                    <option value="4 Star">4 Star</option>
                    <option value="5 Star">5 Star</option>
                    <option value="Budget">Budget</option>
                    <option value="Luxury">Luxury</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meal Plan
                  </label>
                  <select
                    name="mealPlan"
                    value={formData.mealPlan}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="AP">AP (Full Board - B, L, D)</option>
                    <option value="CP">CP (Breakfast)</option>
                    <option value="BB">BB (Bed With Breakfast)</option>
                    <option value="MAP">MAP (Half Board - B, L or D)</option>
                    <option value="MAP_BD">MAP (Breakfast & Dinner)</option>
                    <option value="MAPAI">MAPAI (B, L or D)</option>
                    <option value="CAPAI">CAPAI (Breakfast)</option>
                    <option value="None">None</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Transport & Itinerary - Only for Tour Booking */}
          {formData.leadType === 'tour_booking' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
                🚗 Transport & Itinerary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Type (Select or Type Custom)
                  </label>
                  <input
                    type="text"
                    name="vehicleType"
                    list="vehicleTypeOptions"
                    value={formData.vehicleType}
                    onChange={handleChange}
                    placeholder="Select from list or type custom vehicle type"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                  <datalist id="vehicleTypeOptions">
                    <option value="SIC">SIC (Seat In Coach - Sharing)</option>
                    <option value="PVT">PVT (Private)</option>
                    <option value="FIT">FIT (Fully Independent Traveler)</option>
                    <option value="Sedan">Sedan</option>
                    <option value="SUV">SUV</option>
                    <option value="Tempo Traveller">Tempo Traveller</option>
                    <option value="Mini Bus">Mini Bus</option>
                    <option value="Luxury Coach">Luxury Coach</option>
                    <option value="AC Coach">AC Coach</option>
                  </datalist>
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Click dropdown arrow or start typing to select/enter vehicle type
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tour Plan
                  </label>
                  <input
                    type="text"
                    name="tourPlan"
                    value={formData.tourPlan}
                    onChange={handleChange}
                    placeholder="Brief tour itinerary"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pick-Up Location
                  </label>
                  <input
                    type="text"
                    name="pickUp"
                    value={formData.pickUp}
                    onChange={handleChange}
                    placeholder="e.g., Airport, Hotel"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Drop Location
                  </label>
                  <input
                    type="text"
                    name="drop"
                    value={formData.drop}
                    onChange={handleChange}
                    placeholder="e.g., Airport, Hotel"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Service Requirements - Only for Other Services */}
          {formData.leadType === 'other_services' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
                📝 Service Requirements
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Requirements & Details
                </label>
                <textarea
                  name="specialNote"
                  value={formData.specialNote}
                  onChange={handleChange}
                  rows="5"
                  placeholder="Please describe your service requirements in detail..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )
          }

          {/* Status & Notes */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
              📝 Status & Notes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reservation Status
                </label>
                <select
                  name="reservationStatus"
                  value={formData.reservationStatus}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Pending">Pending</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Special Note
                </label>
                <textarea
                  name="specialNote"
                  value={formData.specialNote}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Any special requirements or notes..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : (editLead ? 'Update Lead' : 'Add Lead')}
            </button>
          </div>
        </form >
      </div >
    </div >
  );
};

export default TourBookingForm;
